<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const emit = defineEmits(['created']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);

const displayName = ref('');
const attributeKey = ref('');
const displayType = ref('text');
const attributeModel = ref('contact_attribute');

const DISPLAY_TYPES = ['text', 'number', 'link', 'date', 'time', 'list', 'checkbox'];

const modelTypes = computed(() => [
  {
    value: 'contact_attribute',
    label: t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.ATTR_MODELS.CONTACT'),
  },
  {
    value: 'conversation_attribute',
    label: t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.ATTR_MODELS.CONVERSATION'),
  },
  {
    value: 'event_attribute',
    label: t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.ATTR_MODELS.EVENT'),
  },
]);

const isCreating = computed(
  () => store.getters['attributes/getUIFlags'].isCreating
);

const isFormValid = computed(
  () => displayName.value.trim() && attributeKey.value.trim()
);

const generateKey = name => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
};

const onNameInput = value => {
  displayName.value = value;
  attributeKey.value = generateKey(value);
};

const resetForm = () => {
  displayName.value = '';
  attributeKey.value = '';
  displayType.value = 'text';
  attributeModel.value = 'contact_attribute';
};

const open = () => {
  resetForm();
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const handleConfirm = async () => {
  try {
    await store.dispatch('attributes/create', {
      attribute_display_name: displayName.value.trim(),
      attribute_key: attributeKey.value.trim(),
      attribute_display_type: displayType.value,
      attribute_model: attributeModel.value,
    });
    useAlert(t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.CREATE_SUCCESS'));
    emit('created');
    close();
  } catch (error) {
    useAlert(
      error.message || t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.CREATE_ERROR')
    );
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.CREATE_ATTRIBUTE')"
    :description="t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.CREATE_ATTRIBUTE_DESC')"
    :confirm-button-label="
      t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.CREATE_ATTRIBUTE')
    "
    :is-loading="isCreating"
    :disable-confirm-button="!isFormValid"
    @confirm="handleConfirm"
    @close="close"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1.5">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.ATTR_FORM.NAME') }}
        </label>
        <input
          :value="displayName"
          type="text"
          :placeholder="
            t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.ATTR_FORM.NAME_PLACEHOLDER')
          "
          class="reset-base h-10 px-3 py-2.5 text-sm rounded-lg outline outline-1 outline-n-slate-6 bg-white dark:bg-n-solid-2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand transition-colors"
          @input="onNameInput($event.target.value)"
        />
      </div>
      <div class="flex flex-col gap-1.5">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.ATTR_FORM.KEY') }}
        </label>
        <input
          v-model="attributeKey"
          type="text"
          class="reset-base h-10 px-3 py-2.5 text-sm rounded-lg outline outline-1 outline-n-slate-6 bg-white dark:bg-n-solid-2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand transition-colors"
        />
      </div>
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.ATTR_FORM.TYPE') }}
          </label>
          <select
            v-model="displayType"
            class="reset-base h-10 px-3 py-2.5 text-sm rounded-lg outline outline-1 outline-n-slate-6 bg-white dark:bg-n-solid-2 text-n-slate-12 focus:outline-n-brand transition-colors"
          >
            <option v-for="dtype in DISPLAY_TYPES" :key="dtype" :value="dtype">
              {{ dtype.charAt(0).toUpperCase() + dtype.slice(1) }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.ATTR_FORM.MODEL') }}
          </label>
          <select
            v-model="attributeModel"
            class="reset-base h-10 px-3 py-2.5 text-sm rounded-lg outline outline-1 outline-n-slate-6 bg-white dark:bg-n-solid-2 text-n-slate-12 focus:outline-n-brand transition-colors"
          >
            <option
              v-for="model in modelTypes"
              :key="model.value"
              :value="model.value"
            >
              {{ model.label }}
            </option>
          </select>
        </div>
      </div>
    </div>
  </Dialog>
</template>
